import React from "react";
import { Link } from "react-router-dom";
import { Check, Monitor, Smartphone, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { getBrand } from "@/lib/products";
import { UNLIMITED_DEVICES, UNLIMITED_DEVICES_LABEL } from "@/lib/constants";

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const defaultPlan = product.plans[0];
  const discount = Math.round(
    (1 - defaultPlan.price / defaultPlan.original) * 100,
  );
  const brand = getBrand(product);

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className={`group relative flex h-full flex-col border bg-white p-6 transition-all duration-200 hover:border-slate-900 hover:shadow-[8px_8px_0_0_rgba(15,23,42,1)] ${
        product.popular
          ? "border-2 border-slate-900"
          : "border-slate-200"
      }`}
    >
      {product.popular && (
        <div className="absolute -top-3 left-6 bg-emerald-500 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-slate-900">
          Most Popular
        </div>
      )}

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="bg-slate-900 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] text-emerald-500">
              {brand}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              {product.category}
            </span>
          </div>
          <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-slate-900">
            {product.name}
          </h3>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          {product.devices === 1 ? <Smartphone className="h-3 w-3" /> : <Monitor className="h-3 w-3" />}
          {product.devices >= UNLIMITED_DEVICES ? UNLIMITED_DEVICES_LABEL : `${product.devices} ${product.devices === 1 ? "Device" : "Devices"}`}
        </div>
      </div>

      <p className="mt-3 text-sm text-slate-600">{product.tagline}</p>

      <ul className="mt-5 space-y-2 text-sm text-slate-700">
        {product.features.slice(0, 4).map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={3} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-6">
        <div className="flex items-end gap-2">
          <div className="font-display text-3xl font-black tracking-tighter text-slate-900">
            ${defaultPlan.price.toFixed(2)}
          </div>
          <div className="pb-1 text-xs text-slate-500">
            <span className="line-through">${defaultPlan.original.toFixed(2)}</span>
            <span className="ml-1 font-bold text-emerald-600">-{discount}%</span>
          </div>
        </div>
        <div className="text-xs text-slate-500">First year. Auto-renews after.</div>

        <div className="mt-4 flex gap-2">
          <Button
            data-testid={`add-to-cart-${product.id}`}
            onClick={() => addItem(product.id, defaultPlan.id, 1)}
            className="flex-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
          >
            Add to Cart
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid={`view-product-${product.id}`}
            className="rounded-sm border-slate-300 hover:border-slate-900"
          >
            <Link to={`/products/${product.id}`}>
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  );
};
